import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Calendar, Plus, Megaphone, Trash2, Pencil, X, Image as ImageIcon,
  ExternalLink, Users, Loader2,
} from "lucide-react";
import ImageUploadField from "../components/ImageUploadField";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function EventCard({ ev, canManage, onEdit, onDelete }) {
  return (
    <div data-testid={`event-row-${ev.id}`} className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-2">
      {ev.image_url && (
        <img src={ev.image_url} alt="" className="w-full h-32 object-cover rounded-lg" />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
            {fmtDateTime(ev.starts_at)}
          </div>
          <div className="text-white font-extrabold text-sm">{ev.title}</div>
          {ev.description && (
            <div className="text-[11px] text-gray-400 whitespace-pre-wrap mt-1">{ev.description}</div>
          )}
          {ev.link_url && (
            <a href={ev.link_url} target="_blank" rel="noreferrer"
               className="text-[11px] text-[#FF6B00] inline-flex items-center gap-1 mt-2"
               data-testid={`event-link-${ev.id}`}>
              <ExternalLink className="w-3 h-3" /> Acessar
            </a>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1">
            <button data-testid={`event-edit-${ev.id}`} onClick={() => onEdit(ev)}
                    className="p-1.5 rounded bg-[#1F2330] border border-[#3E4760] text-gray-300 hover:text-white">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button data-testid={`event-delete-${ev.id}`} onClick={() => onDelete(ev)}
                    className="p-1.5 rounded bg-red-600/10 border border-red-600/40 text-red-300 hover:bg-red-600/20">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EventForm({ draft, mentorships, onChange, onCancel, onSubmit, busy }) {
  return (
    <div className="bg-[#1A1D26] border border-[#282D3D] rounded-xl p-4 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="text-white font-extrabold">
          {draft.id ? "Editar evento" : "Novo evento"}
        </div>
        <button onClick={onCancel} className="text-gray-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      {mentorships.length > 1 && !draft.id && (
        <select
          data-testid="event-form-mentorship"
          value={draft.mentorship_id || ""}
          onChange={(e) => onChange({ ...draft, mentorship_id: e.target.value })}
          className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
        >
          <option value="">— Mentoria —</option>
          {mentorships.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      )}
      <input
        data-testid="event-form-title"
        value={draft.title || ""}
        onChange={(e) => onChange({ ...draft, title: e.target.value })}
        placeholder="Título"
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <input
        data-testid="event-form-starts-at"
        type="datetime-local"
        value={draft.starts_at ? draft.starts_at.slice(0, 16) : ""}
        onChange={(e) => onChange({ ...draft, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <textarea
        data-testid="event-form-description"
        value={draft.description || ""}
        onChange={(e) => onChange({ ...draft, description: e.target.value })}
        placeholder="Descrição (opcional)"
        rows={2}
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <ImageUploadField
        testId="event-form-image"
        value={draft.image_url || ""}
        onChange={(url) => onChange({ ...draft, image_url: url })}
        placeholder="URL de imagem (opcional)"
      />
      <input
        data-testid="event-form-link"
        value={draft.link_url || ""}
        onChange={(e) => onChange({ ...draft, link_url: e.target.value })}
        placeholder="Link da aula ao vivo (opcional)"
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-300 bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2">
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="event-form-notify" checked={Boolean(draft.notify_students)}
            onChange={(e) => onChange({ ...draft, notify_students: e.target.checked })} className="accent-[#FF6B00]" />
          Notificar alunos (push) antes do evento
        </label>
        {draft.notify_students && (
          <select data-testid="event-form-notify-minutes" value={draft.notify_minutes_before || 60}
            onChange={(e) => onChange({ ...draft, notify_minutes_before: Number(e.target.value) })}
            className="bg-[#14161C] border border-[#282D3D] rounded px-1 py-1 text-white">
            {[[15, "15 min"], [30, "30 min"], [60, "1 hora"], [180, "3 horas"], [1440, "1 dia"]].map(([v, l]) => (
              <option key={v} value={v}>{l} antes</option>
            ))}
          </select>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel}
                className="px-3 py-1.5 rounded-lg text-xs border border-[#3E4760] text-gray-300">
          Cancelar
        </button>
        <button
          data-testid="event-form-submit"
          onClick={onSubmit}
          disabled={busy || !draft.title || !draft.starts_at || (!draft.id && !draft.mentorship_id)}
          className="px-3 py-1.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase disabled:opacity-60 flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {draft.id ? "Salvar" : "Criar evento"}
        </button>
      </div>
    </div>
  );
}

function AnnouncementForm({ draft, mentorships, isOwner, onChange, onCancel, onSubmit, onPreview, busy }) {
  return (
    <div className="bg-[#1A1D26] border border-[#282D3D] rounded-xl p-4 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="text-white font-extrabold">{draft.id ? "Editar comunicado" : "Novo comunicado"}</div>
        <button onClick={onCancel} className="text-gray-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        data-testid="ann-form-title"
        value={draft.title || ""}
        onChange={(e) => onChange({ ...draft, title: e.target.value })}
        placeholder="Título"
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <textarea
        data-testid="ann-form-message"
        value={draft.message || ""}
        onChange={(e) => onChange({ ...draft, message: e.target.value })}
        placeholder="Mensagem"
        rows={3}
        className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
      />
      <ImageUploadField
        testId="ann-form-image"
        value={draft.image_url || ""}
        onChange={(url) => onChange({ ...draft, image_url: url })}
        placeholder="URL de imagem / print (opcional)"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          data-testid="ann-form-cta-label"
          value={draft.cta_label || ""}
          onChange={(e) => onChange({ ...draft, cta_label: e.target.value })}
          placeholder="CTA (opcional)"
          className="bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
        />
        <input
          data-testid="ann-form-cta-url"
          value={draft.cta_url || ""}
          onChange={(e) => onChange({ ...draft, cta_url: e.target.value })}
          placeholder="URL do CTA"
          className="bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
        />
      </div>
      <div>
        <div className="text-[11px] font-bold text-gray-300 mb-1">Público-alvo</div>
        <div className="space-y-1 bg-[#0F1218] border border-[#282D3D] rounded-lg p-2 max-h-32 overflow-auto">
          {isOwner && (
            <label className="flex items-center gap-2 text-[11px] text-gray-300" data-testid="ann-audience-all">
              <input
                type="checkbox"
                checked={!!draft.all_students}
                onChange={(e) => onChange({ ...draft, all_students: e.target.checked, mentorship_ids: [] })}
              />
              <strong>Todos os alunos (todas as mentorias)</strong>
            </label>
          )}
          {!draft.all_students && mentorships.map((m) => {
            const checked = (draft.mentorship_ids || []).includes(m.id);
            return (
              <label key={m.id} className="flex items-center gap-2 text-[11px] text-gray-300"
                     data-testid={`ann-audience-${m.id}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const list = new Set(draft.mentorship_ids || []);
                    if (checked) list.delete(m.id); else list.add(m.id);
                    onChange({ ...draft, mentorship_ids: Array.from(list) });
                  }}
                />
                {m.name}
              </label>
            );
          })}
        </div>
      </div>
      {!draft.id && (
        <label className="flex items-center gap-2 text-[11px] text-gray-300">
          <input type="checkbox" data-testid="ann-form-send-push" checked={Boolean(draft.send_push)}
            onChange={(e) => onChange({ ...draft, send_push: e.target.checked })} className="accent-[#FF6B00]" />
          Enviar também como notificação push
        </label>
      )}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button data-testid="ann-form-preview" onClick={() => onPreview(draft)}
                className="px-3 py-1.5 rounded-lg bg-[#1F2330] border border-[#3E4760] text-white text-xs font-extrabold uppercase">
          Visualizar
        </button>
        <button
          data-testid="ann-form-submit"
          onClick={onSubmit}
          disabled={busy || !draft.title || !draft.message}
          className="px-3 py-1.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase disabled:opacity-60 flex items-center gap-1"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
          {draft.id ? "Salvar" : "Publicar"}
        </button>
      </div>
    </div>
  );
}

export default function AgendaPage() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "platform_owner";
  const isOwner = user?.role === "platform_owner";

  const [tab, setTab] = useState("upcoming"); // upcoming | past | announcements
  const [events, setEvents] = useState({ upcoming: [], past: [], next: null });
  const [anns, setAnns] = useState([]);
  const [mentorships, setMentorships] = useState([]);

  const [eventDraft, setEventDraft] = useState(null);
  const [annDraft, setAnnDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadMentorships = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await api.get("/api/admin/mentorships");
      setMentorships(res.data?.mentorships || []);
    } catch (_e) {}
  }, [isManager]);

  const loadData = useCallback(async () => {
    try {
      const [ev, an] = await Promise.all([
        api.get("/api/events"),
        api.get(`/api/announcements?scope=${isManager ? "managed" : "mine"}`),
      ]);
      setEvents(ev.data || {});
      setAnns(an.data?.announcements || []);
    } catch (_e) {}
  }, [isManager]);

  useEffect(() => { loadMentorships(); loadData(); }, [loadMentorships, loadData]);

  const submitEvent = async () => {
    if (!eventDraft) return;
    setBusy(true);
    try {
      if (eventDraft.id) {
        await api.patch(`/api/events/${eventDraft.id}`, eventDraft);
      } else {
        await api.post("/api/events", eventDraft);
      }
      setEventDraft(null);
      await loadData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao salvar evento.");
    } finally {
      setBusy(false);
    }
  };

  const removeEvent = async (ev) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o evento "${ev.title}"?`)) return;
    await api.delete(`/api/events/${ev.id}`);
    await loadData();
  };

  const submitAnn = async () => {
    if (!annDraft) return;
    setBusy(true);
    try {
      if (annDraft.id) {
        await api.patch(`/api/announcements/${annDraft.id}`, annDraft);
      } else {
        await api.post("/api/announcements", annDraft);
      }
      setAnnDraft(null);
      await loadData();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao publicar comunicado.");
    } finally {
      setBusy(false);
    }
  };

  const previewAnn = async (draft) => {
    try {
      const res = await api.post("/api/announcements/preview", draft);
      setPreview(res.data);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Falha ao gerar prévia.");
    }
  };

  const removeAnn = async (a) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir comunicado "${a.title}"?`)) return;
    await api.delete(`/api/announcements/${a.id}`);
    await loadData();
  };

  const defaultMentorshipId = useMemo(
    () => (mentorships.length === 1 ? mentorships[0].id : ""),
    [mentorships],
  );

  return (
    <div data-testid="agenda-page" className="max-w-3xl mx-auto px-4 py-4 pb-24 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#FF6B00]" />
          <h1 className="text-xl font-extrabold text-white">Agenda</h1>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <button
              data-testid="create-event-btn"
              onClick={() => setEventDraft({
                mentorship_id: defaultMentorshipId,
                title: "", description: "", starts_at: "", image_url: "", link_url: "",
              })}
              className="px-3 py-1.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-[11px] uppercase flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Criar evento
            </button>
            <button
              data-testid="create-ann-btn"
              onClick={() => setAnnDraft({
                title: "", message: "", image_url: "", cta_label: "", cta_url: "",
                mentorship_ids: isOwner ? [] : (defaultMentorshipId ? [defaultMentorshipId] : []),
                all_students: false, status: "published",
              })}
              className="px-3 py-1.5 rounded-lg bg-[#1F2330] border border-[#3E4760] hover:bg-[#2A3041] text-white font-extrabold text-[11px] uppercase flex items-center gap-1"
            >
              <Megaphone className="w-3.5 h-3.5" /> Comunicado
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#282D3D] overflow-x-auto">
        {[
          { k: "upcoming", label: `Próximos (${events.upcoming?.length || 0})` },
          { k: "past", label: `Histórico (${events.past?.length || 0})` },
          { k: "announcements", label: `Comunicados (${anns.length})` },
        ].map(({ k, label }) => (
          <button
            key={k}
            data-testid={`agenda-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === k ? "border-[#FF6B00] text-[#FF6B00]" : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {eventDraft && (
        <EventForm
          draft={eventDraft}
          mentorships={mentorships}
          onChange={setEventDraft}
          onCancel={() => setEventDraft(null)}
          onSubmit={submitEvent}
          busy={busy}
        />
      )}
      {annDraft && (
        <AnnouncementForm
          draft={annDraft}
          mentorships={mentorships}
          isOwner={isOwner}
          onChange={setAnnDraft}
          onCancel={() => setAnnDraft(null)}
          onSubmit={submitAnn}
          onPreview={previewAnn}
          busy={busy}
        />
      )}

      {tab === "upcoming" && (
        <div className="space-y-3" data-testid="upcoming-events-list">
          {events.next && (
            <div className="bg-gradient-to-br from-[#FF6B00]/15 to-[#1F2330] border border-[#FF6B00]/40 rounded-xl p-4"
                 data-testid="next-event-card">
              <div className="text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider mb-1">Próximo evento</div>
              <div className="text-white font-extrabold text-base">{events.next.title}</div>
              <div className="text-xs text-gray-300 mt-1">{fmtDateTime(events.next.starts_at)}</div>
              {events.next.link_url && (
                <a href={events.next.link_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold text-[#FF6B00]">
                  <ExternalLink className="w-3 h-3" /> Acessar
                </a>
              )}
            </div>
          )}
          {(events.upcoming || []).length === 0 && (
            <div className="text-xs text-gray-500">Nenhum evento programado.</div>
          )}
          {(events.upcoming || []).map((ev) => (
            <EventCard key={ev.id} ev={ev} canManage={isManager}
                       onEdit={(e) => setEventDraft(e)} onDelete={removeEvent} />
          ))}
        </div>
      )}

      {tab === "past" && (
        <div className="space-y-3" data-testid="past-events-list">
          {(events.past || []).length === 0 && (
            <div className="text-xs text-gray-500">Nenhum evento no histórico.</div>
          )}
          {(events.past || []).map((ev) => (
            <EventCard key={ev.id} ev={ev} canManage={isManager}
                       onEdit={(e) => setEventDraft(e)} onDelete={removeEvent} />
          ))}
        </div>
      )}

      {tab === "announcements" && (
        <div className="space-y-3" data-testid="announcements-list">
          {anns.length === 0 && (
            <div className="text-xs text-gray-500">Nenhum comunicado.</div>
          )}
          {anns.map((a) => (
            <div key={a.id} className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-2"
                 data-testid={`ann-row-${a.id}`}>
              {a.image_url && (
                <img src={a.image_url} alt="" className="w-full h-32 object-cover rounded-lg" />
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-extrabold text-sm">{a.title}</div>
                  <div className="text-[11px] text-gray-300 whitespace-pre-wrap mt-1">{a.message}</div>
                  <div className="text-[10px] text-gray-500 mt-2 flex items-center gap-1">
                    <Users className="w-3 h-3" /> {a.audience_label || "—"} · {fmtDateTime(a.published_at)}
                  </div>
                  {a.cta_url && (
                    <a href={a.cta_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold text-[#FF6B00]"
                       data-testid={`ann-cta-${a.id}`}>
                      <ExternalLink className="w-3 h-3" /> {a.cta_label || "Saiba mais"}
                    </a>
                  )}
                </div>
                {isManager && (a.created_by === user?.id || isOwner) && (
                  <div className="flex items-center gap-1">
                    <button data-testid={`ann-edit-${a.id}`} onClick={() => setAnnDraft({ ...a })}
                            className="p-1.5 rounded bg-[#1F2330] border border-[#3E4760] text-gray-300 hover:text-white">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`ann-delete-${a.id}`} onClick={() => removeAnn(a)}
                            className="p-1.5 rounded bg-red-600/10 border border-red-600/40 text-red-300 hover:bg-red-600/20">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setPreview(null)} data-testid="ann-preview-overlay">
          <div className="bg-[#14161C] border border-[#FF6B00]/40 rounded-2xl max-w-md w-full p-5 space-y-2"
               onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
              Prévia — {preview.audience_label}
            </div>
            {preview.image_url && (
              <img src={preview.image_url} alt="" className="w-full h-36 object-cover rounded-lg" />
            )}
            <div className="text-white font-extrabold text-base">{preview.title}</div>
            <div className="text-[12px] text-gray-300 whitespace-pre-wrap">{preview.message}</div>
            {preview.cta_url && (
              <a href={preview.cta_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-[11px] font-bold text-[#FF6B00]">
                <ExternalLink className="w-3 h-3" /> {preview.cta_label || "Saiba mais"}
              </a>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={() => setPreview(null)}
                      className="px-3 py-1.5 rounded-lg bg-[#1F2330] border border-[#3E4760] text-white text-xs font-extrabold uppercase">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
